import { useEffect, useMemo, useRef, useState } from "react";
import CameraPane from "../components/CameraPane";
import { fetchFrame, nextDelay } from "../lib/liveFrames";
import { useApi } from "../context/AppContext";

// What each camera is for, so a pane is identifiable at a glance. Falls back
// to "camera N" for anything unmapped rather than guessing.
// Until a cabinet's type is read, the chest's usual layout; then the type's mounts.
const CAMERA_LABELS = { 0: "camera 0 — top", 1: "camera 1 — bottom" };

// The knobs that actually decide a charge today. The previous set —
// confidence_threshold, consecutive_required, inference_max_fps,
// motion_min_area — belonged to the YOLO-per-frame pipeline that was retired
// in August; the runtime ignores every one of them, so showing them invited
// tuning a number that does nothing.
const GATE_FIELDS = [
  {
    key: "episode_min_travel",
    label: "Min travel",
    step: 0.01, min: 0, max: 3,
    help: "How far the region moved, in multiples of its own size. A hand carrying a product travels; the hole it left cannot. 26 of 30 episodes on 2 Sep were rejected here.",
  },
  {
    key: "episode_max_region_frac",
    label: "Max region (fraction of frame)",
    step: 0.001, min: 0, max: 0.5,
    help: "A product in a hand is small. Correct charges measured 0.0017–0.0118 of frame; the four wrong charges of 2 Sep measured 0.029–0.040.",
  },
  {
    key: "episode_min_support",
    label: "Min support (frames)",
    step: 1, min: 1, max: 10,
    help: "Frames that independently agree with the winner. One frame deciding a charge is how a motion smear billed the wrong product.",
  },
  {
    key: "background_absorb_after_s",
    label: "Background absorb after (s)",
    step: 5, min: 5, max: 900,
    help: "How long a change may persist before the reference learns it. Too short and the hole a taken product left becomes 'background', then gets recognised as a product.",
  },
  {
    key: "background_min_area_frac",
    label: "Background min area (fraction)",
    step: 0.0005, min: 0, max: 0.2,
    help: "Smallest change worth calling a region.",
  },
  {
    key: "door_open_level",
    label: "Door opens above (light)",
    step: 0.01, min: 0, max: 1,
    help: "Mean brightness of the bottom camera, 0–1. A closed cabinet is a dark box. Watch the live level in the header with the door shut and again with it open, then set this between the two readings.",
  },
  {
    key: "door_close_level",
    label: "Door closes below (light)",
    step: 0.01, min: 0, max: 1,
    help: "The lower half of the hysteresis. Between the two the door decides nothing, which is what stops a session flapping while a hand is in the gap.",
  },
  {
    key: "live_fps",
    label: "Live view FPS",
    step: 1, min: 1, max: 30,
    help: "Frames the device publishes for the live view. Costs board time; it does not affect recognition.",
  },
  {
    key: "live_max_width",
    label: "Live view width (px)",
    step: 160, min: 320, max: 1920,
    help: "Widest side of a live frame. 640 by default; the cameras capture about 2000, so raise it (1280) to read a label in the full screen view, and lower it again after - it costs upload, not recognition.",
  },
];

// Only sent when the operator edits them — an empty field is left alone rather
// than pushed as a zero.
const DEFAULT_CONFIG = {};

function Section({ title, children, right, className = "" }) {
  // `min-w-0` is load-bearing: a grid item defaults to `min-width: auto`, so a
  // child that cannot shrink — a table, a monospace log line — widens the
  // column past its fraction and the whole page scrolls sideways instead of
  // the one panel that is too wide.
  return (
    <section className={`min-w-0 rounded-lg border border-slate-200 bg-white p-4 ${className}`}>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-base font-semibold">{title}</h2>
        {right}
      </div>
      {children}
    </section>
  );
}

// One fact and the number behind it. The number is the point: "background:
// clean" is an opinion, "cam0 0.12 cam1 0.40" is what it rests on.
function Chip({ label, value, detail, tone = "idle" }) {
  const tones = {
    good: "border-emerald-300 bg-emerald-50 text-emerald-900",
    warn: "border-amber-300 bg-amber-50 text-amber-900",
    bad: "border-rose-300 bg-rose-50 text-rose-900",
    off: "border-slate-300 bg-slate-50 text-slate-600",
    idle: "border-slate-200 bg-white text-slate-700",
    unknown: "border-slate-200 bg-white text-slate-400",
  };
  return (
    <div className={`min-w-0 flex-1 basis-40 rounded-lg border px-3 py-2 ${tones[tone] || tones.idle}`}>
      <div className="text-[10px] uppercase tracking-wide opacity-70">{label}</div>
      <div className="truncate text-sm font-semibold leading-tight" title={String(value)}>
        {value}
      </div>
      {detail && <div className="mt-0.5 text-[10px] leading-snug opacity-70">{detail}</div>}
    </div>
  );
}

function Notice({ tone = "info", children }) {
  if (!children) return null;
  const cls =
    tone === "error"
      ? "bg-rose-50 text-rose-700 border-rose-200"
      : tone === "success"
        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
        : "bg-sky-50 text-sky-700 border-sky-200";
  return <div className={`rounded border px-3 py-2 text-sm ${cls}`}>{children}</div>;
}

// What the opening review reads right now, from the live overlay's review block
// (docs/tasks/35): per product and camera, the verdict so far and the best cos
// of its newest windows. Empty until the cabinet's runtime publishes it.
const VERDICT_TONE = { taken: "bg-emerald-100 text-emerald-800", returned: "bg-sky-100 text-sky-800", touched: "bg-amber-100 text-amber-800" };
// Recording for training (operator, 25 Oct; docs/tasks/39): full-frame
// recordings of staff test purchases streamed to the cloud. One click, only the
// two keys - not the whole edited config the Gates panel pushes. The state is
// read back from the cabinet's latest "gates" entry, so the badge shows what
// the cabinet does, not what was clicked. A staff test facility: frames of a
// purchase leave the cabinet, so it must be off on any cabinet that serves
// customers (CLAUDE.md, "No face leaves a cabinet").
// With a runtime that knows the time limit (its gates carry
// session_recording_cloud_stream_until), it is switched on for a while and
// switches itself off; an older runtime gets the plain on, which stays on
// until someone switches it off - restarts included.
const RECORDING_FOR = [[1800, "for 30 min"], [7200, "for 2 h"], [0, "until switched off"]];

function RecordingForTraining({ deviceId, gates, send }) {
  const [pending, setPending] = useState(null); // {want, at}
  const [err, setErr] = useState(null);
  const [forS, setForS] = useState(1800);
  const [now, setNow] = useState(() => Date.now() / 1000);
  const on = !!gates?.session_recording_cloud_stream && gates?.session_recording_enabled !== false;
  const known = !!gates;
  const timed = gates != null && Object.prototype.hasOwnProperty.call(gates, "session_recording_cloud_stream_until");
  const until = Number(gates?.session_recording_cloud_stream_until || 0);
  useEffect(() => { if (pending && known && on === pending.want) setPending(null); }, [on, known, pending]);
  useEffect(() => { setPending(null); setErr(null); }, [deviceId]);
  useEffect(() => {
    if (!on || !until) return undefined;
    const t = setInterval(() => setNow(Date.now() / 1000), 1000);
    return () => clearInterval(t);
  }, [on, until]);
  const left = on && until ? Math.max(0, until - now) : null;
  const leftText = left == null ? "" : left >= 3600 ? `${Math.floor(left / 3600)} h ${Math.round((left % 3600) / 60)} min left` : `${Math.ceil(left / 60)} min left`;
  const toggle = async () => {
    const want = !on;
    const span = timed ? forS : 0;
    if (want && !window.confirm(
      "Switch ON recording for training?\n\n"
      + "Every opening of this cabinet is recorded in full - every frame of every camera - and uploaded to the cloud when the door shuts. "
      + "Faces and anything else in view leave the cabinet.\n\n"
      + "Staff test purchases only. It must be OFF on any cabinet that serves customers.\n\n"
      + (span ? `It switches itself off after ${span / 60} minutes.` : "It stays on - also across restarts - until someone switches it off."))) return;
    if (!want && !window.confirm("Switch OFF recording for training? Recordings stop going to the cloud; local recording stays as it is.")) return;
    setErr(null);
    try {
      await send(!want ? { session_recording_cloud_stream: false }
        : span ? { session_recording_enabled: true, session_recording_cloud_stream_for_s: span }
        : { session_recording_enabled: true, session_recording_cloud_stream: true });
      setPending({ want, at: Date.now() });
    } catch (e) {
      setErr(e.message || String(e));
    }
  };
  return (
    <div className="flex items-center gap-2">
      {on ? (
        <span className="animate-pulse rounded bg-rose-600 px-2 py-1 text-xs font-bold tracking-wide text-white" title="Full frames of every opening go to the cloud. Staff tests only.">
          ● RECORDING → CLOUD{leftText ? ` · ${leftText}` : timed ? " · no time limit" : ""}
        </span>
      ) : (
        <span className="rounded bg-slate-100 px-2 py-1 text-xs text-slate-600">{known ? "Recording for training: off" : "Recording for training: unknown"}</span>
      )}
      {!on && timed && (
        <select value={forS} onChange={(e) => setForS(Number(e.target.value))} disabled={!!pending}
          className="rounded border border-slate-300 px-1 py-1 text-xs">
          {RECORDING_FOR.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
        </select>
      )}
      <button
        onClick={toggle}
        disabled={!deviceId || !!pending}
        className={`rounded border px-2 py-1 text-xs font-medium disabled:opacity-50 ${on ? "border-rose-300 bg-white text-rose-700 hover:bg-rose-50" : "border-slate-300 bg-white hover:bg-slate-50"}`}
      >
        {pending ? (pending.want ? "Switching on…" : "Switching off…") : on ? "Stop recording for training" : "Record for training"}
      </button>
      {pending && Date.now() - pending.at > 30000 && <span className="text-[10px] text-amber-700">the cabinet has not confirmed yet</span>}
      {err && <span className="text-[10px] text-rose-700">{err}</span>}
    </div>
  );
}

function ReviewNow({ cameras, names, labels }) {
  const rows = [];
  let lag = null;
  for (const c of cameras || []) {
    const rv = c.overlay?.review;
    if (!rv) continue;
    lag = lag == null ? rv.lag_s : Math.max(lag, rv.lag_s || 0);
    const best = {};
    for (const h of rv.hits || []) best[h.product] = Math.max(best[h.product] || 0, h.cos || 0);
    const ids = new Set([...Object.keys(rv.products || {}), ...Object.keys(best)]);
    for (const id of ids) rows.push({ cam: c.camera, id, verdict: rv.products?.[id]?.verdict, detail: rv.products?.[id]?.detail, cos: best[id] });
  }
  if (!rows.length) return null;
  const nameOf = (id) => names[id]?.name || names[String(id).slice(0, 8)]?.name || id;
  return (
    <div className="rounded border border-violet-200 bg-violet-50/50 p-2 text-xs sm:col-span-2">
      <div className="mb-1 font-medium text-violet-900">What the review sees now{lag != null ? ` · ${Number(lag).toFixed(1)} s behind the frame` : ""}</div>
      <table className="w-full">
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.cam}-${r.id}`} className="border-t border-violet-100">
              <td className="py-0.5 pr-2 text-slate-500">{labels?.[r.cam] || `camera ${r.cam}`}</td>
              <td className="pr-2 font-medium">{nameOf(r.id)}</td>
              <td className="pr-2">{r.verdict ? <span className={`rounded px-1.5 py-0.5 ${VERDICT_TONE[r.verdict] || "bg-slate-100 text-slate-700"}`}>{r.verdict}</span> : "—"}</td>
              <td className="pr-2 font-mono">{r.cos ? r.cos.toFixed(2) : ""}</td>
              <td className="truncate text-slate-500" title={r.detail}>{r.detail || ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// One camera full screen, for looking at detail. The small panes are for
// comparing the two viewpoints at once; at that size the chips and box labels
// cover what the operator is trying to see, so here each layer can be hidden.
// Live frame width the full screen view can ask the cabinet for. One knob for
// all its cameras (live_max_width, runtime b3d5972+); the cameras capture at
// about 2000 px, the live view sends 640 unless asked.
const LIVE_WIDTHS = [[640, "standard · 640 px"], [1280, "high · 1280 px"], [1920, "max · 1920 px"]];

function BigCameraView({ cameras, camera, onCamera, onClose, names, labels, rotation, freshness,
  calibration = null, correctedView = {}, onToggleCorrected = null, gates = null, send = null }) {
  const [showBoxes, setShowBoxes] = useState(true);
  const [showText, setShowText] = useState(false);
  // Resolution: what the cabinet reports (gates.live_max_width) and what was
  // asked here and not yet confirmed. A runtime without the knob does not
  // report it, and the switch says so instead of sending a key it ignores.
  const deviceWidth = gates && gates.live_max_width != null ? Number(gates.live_max_width) : null;
  const [wantWidth, setWantWidth] = useState(null);
  const [widthErr, setWidthErr] = useState(null);
  const raisedHere = useRef(false);
  const sendRef = useRef(send);
  sendRef.current = send;
  useEffect(() => { if (wantWidth != null && deviceWidth === wantWidth) setWantWidth(null); }, [deviceWidth, wantWidth]);
  const setWidth = async (w) => {
    if (!send) return;
    setWidthErr(null);
    setWantWidth(w);
    try {
      await send({ live_max_width: w });
      raisedHere.current = w > 640;
    } catch (e) {
      setWantWidth(null);
      setWidthErr(e.message || String(e));
    }
  };
  // A raised width costs upload on every frame for as long as it stays, so
  // leaving the full screen view puts back what this view raised. Closing the
  // tab does not; the Gates panel shows the value and sets it back.
  useEffect(() => () => {
    if (raisedHere.current && sendRef.current) sendRef.current({ live_max_width: 640 }).catch(() => {});
  }, []);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      const i = cameras.findIndex((c) => c.camera === camera);
      if (e.key === "ArrowRight" && cameras.length > 1) onCamera(cameras[(i + 1) % cameras.length].camera);
      if (e.key === "ArrowLeft" && cameras.length > 1) onCamera(cameras[(i - 1 + cameras.length) % cameras.length].camera);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [cameras, camera, onCamera, onClose]);
  const c = cameras.find((x) => x.camera === camera);
  if (!c) return null;
  const stale = freshness(c.camera);
  const label = labels[c.camera] || `camera ${c.camera}`;
  const toggle = "flex items-center gap-1 rounded border border-white/20 px-2 py-0.5";
  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-slate-950/95 p-3 text-sm text-white" role="dialog" aria-label={`${label} full screen`}>
      <div className="mb-2 flex flex-wrap items-center gap-2">
        {cameras.map((x) => (
          <button key={x.camera} onClick={() => onCamera(x.camera)}
            className={`rounded px-2 py-0.5 ${x.camera === camera ? "bg-white text-slate-900" : "border border-white/30 hover:bg-white/10"}`}>
            {labels[x.camera] || `camera ${x.camera}`}
          </button>
        ))}
        <label className={toggle}><input type="checkbox" checked={showBoxes} onChange={(e) => setShowBoxes(e.target.checked)} /> boxes</label>
        <label className={toggle}><input type="checkbox" checked={showText} onChange={(e) => setShowText(e.target.checked)} /> texts</label>
        {calibration?.cameras?.[String(c.camera)] ? (
          <label className={toggle} title="Fisheye straightened and white balance neutralised from the cabinet's measured lens and light - display only">
            <input type="checkbox" checked={!!correctedView[c.camera]} onChange={() => onToggleCorrected && onToggleCorrected(c.camera)} /> corrected
          </label>
        ) : null}
        <label className={toggle} title={deviceWidth == null
          ? "This cabinet's runtime does not report live_max_width yet: it sends 640 px whatever is asked."
          : "Width of the live frames the cabinet sends, all cameras. Raised here, put back to 640 when this view closes."}>
          resolution
          <select
            value={wantWidth ?? deviceWidth ?? 640}
            disabled={deviceWidth == null || wantWidth != null || !send}
            onChange={(e) => setWidth(Number(e.target.value))}
            className="rounded bg-slate-800 px-1 text-xs text-white disabled:opacity-50"
          >
            {LIVE_WIDTHS.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
            {deviceWidth != null && !LIVE_WIDTHS.some(([v]) => v === deviceWidth) ? <option value={deviceWidth}>{deviceWidth} px</option> : null}
          </select>
        </label>
        {deviceWidth == null ? <span className="text-xs text-amber-300">resolution switch needs a newer runtime on this cabinet</span> : null}
        {wantWidth != null ? <span className="text-xs text-amber-300">asking the cabinet for {wantWidth} px…</span> : null}
        {widthErr ? <span className="text-xs text-rose-300">{widthErr}</span> : null}
        <span className="text-xs text-white/60">
          {c.overlay?.focus ? `focus ${c.overlay.focus.sharp} / need ${c.overlay.focus.need} · ` : ""}
          {c.overlay?.ts ? `frame ${new Date(c.overlay.ts * 1000).toLocaleTimeString()}` : ""}
        </span>
        {stale ? (
          <span className="rounded bg-rose-600 px-2 py-0.5 text-xs">no new frame for {Math.round(stale.age)} s — {stale.why}</span>
        ) : null}
        <button onClick={onClose} className="ml-auto rounded border border-white/30 px-2 py-0.5 hover:bg-white/10">Close (Esc)</button>
      </div>
      <div className="relative min-h-0 flex-1">
        <CameraPane
          large
          cameraId={c.camera}
          frameUrl={c.url}
          overlay={c.overlay}
          names={names}
          label={label}
          rotation={rotation[c.camera] ?? rotation[String(c.camera)] ?? 0}
          showBoxes={showBoxes}
          showText={showText}
          calibration={calibration?.cameras?.[String(c.camera)] || null}
          corrected={!!correctedView[c.camera]}
        />
      </div>
      <p className="mt-1 text-xs text-white/50">
        ← → switch camera. The picture is the live view's own frame, as the cabinet publishes it
        (640 px unless the resolution switch above asks for more) — enlarging it here does not add detail the cabinet did not send.
        {calibration ? " \"Corrected\" straightens the fisheye and neutralises the white balance from the cabinet's measured lens and light; the cabinet itself keeps working on the native frame." : ""}
      </p>
    </div>
  );
}

// fixedDeviceId: embedded in a cabinet's page (docs/tasks/35) - no device picker.
export function TestConsolePage({ fixedDeviceId = null, analysisMode = null } = {}) {
  const api = useApi();

  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState(fixedDeviceId || "");
  const [cameraLabels, setCameraLabels] = useState(CAMERA_LABELS);
  // Degrees clockwise to show each camera upright (health
  // cameras.display_rotation): a side-wall camera is sideways by design.
  const [cameraRotation, setCameraRotation] = useState({});
  // The camera shown full screen (Live view's expand button), or null.
  const [bigCamera, setBigCamera] = useState(null);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);

  // Session / commands
  const [externalSession, setExternalSession] = useState("");
  const [recentCommands, setRecentCommands] = useState([]);
  const [sessionActive, setSessionActive] = useState(false);
  // Wall-clock time (epoch sec) when the operator most recently clicked
  // Start in *this* tab. Used to suppress the "no events" warning during the
  // first few seconds while we wait for the daemon to ack and start pushing.
  const [sessionStartedAt, setSessionStartedAt] = useState(0);
  const [staticMode, setStaticMode] = useState(false);

  // Pipeline log
  const [pipelineLog, setPipelineLog] = useState([]);

  // Lookup: product_uuid → { ean_code, name }. Loaded once so we can show
  // human-readable detection info instead of bare UUIDs.
  const [productsByUuid, setProductsByUuid] = useState({});
  // Events at or before this are ignored when building the basket. The device
  // cart is cleared too, but the pipeline log keeps its history, so without a
  // watermark the view would immediately re-count the same events.
  const [basketClearedAt, setBasketClearedAt] = useState(0);

  // Live stream
  const [liveOn, setLiveOn] = useState(false);
  // One entry per camera: { camera, url, overlay }. A cabinet is watched from
  // above and below and a pickup is judged from both at once, so this is a
  // list rather than a single frame.
  const [cameras, setCameras] = useState([]);
  // Per camera: the SAS URL, the ETag of the frame on screen, its timestamp,
  // and the object URL currently displayed. Kept in a ref rather than state
  // because it changes many times a second and none of it is rendered
  // directly — the frame the operator sees is committed to `cameras`.
  const frameSourcesRef = useRef(new Map());
  // Whether reading the blob directly works. It needs CORS on the storage
  // account; where that is missing this drops back to the old path rather
  // than leaving the console with no picture at all.
  const directFrameRef = useRef(true);

  // Config
  const [config, setConfig] = useState(DEFAULT_CONFIG);

  // -- load devices once --
  useEffect(() => {
    api
      .listDevices()
      .then((data) => {
        const list = Array.isArray(data) ? data : data?.data || [];
        setDevices(list);
        if (list.length > 0 && !deviceId && !fixedDeviceId) setDeviceId(list[0].id || list[0].device_id || "");
      })
      .catch((e) => setError(e.message || "failed to load devices"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The camera labels are the cabinet type's mounts, not a guess.
  useEffect(() => {
    if (!deviceId) return;
    api.getDeviceCabinetType(deviceId).then((r) => {
      const cams = r?.cabinet_type?.cameras || [];
      if (!cams.length) { setCameraLabels(CAMERA_LABELS); return; }
      setCameraLabels(Object.fromEntries(cams.map((c) => [c.id, `camera ${c.id}${c.mount ? ` — ${c.mount}` : ""}`])));
    }).catch(() => setCameraLabels(CAMERA_LABELS));
  }, [api, deviceId]);

  // The cabinet's measured lens and light (docs/tasks/39), when it was measured:
  // the per-camera "corrected" view draws from it. Nothing without it.
  const [calibration, setCalibration] = useState(null);
  const [correctedView, setCorrectedView] = useState({});
  const toggleCorrected = (cam) => setCorrectedView((v) => ({ ...v, [cam]: !v[cam] }));
  useEffect(() => {
    if (!deviceId) return undefined;
    let gone = false;
    setCalibration(null);
    setCorrectedView({});
    api.getDeviceCalibration(deviceId)
      .then((r) => { if (!gone) setCalibration(r?.calibration || null); })
      .catch(() => {});
    return () => { gone = true; };
  }, [api, deviceId]);

  // How each camera is shown upright, from the cabinet's own health report.
  // Re-read now and then: a mount is changed rarely, but on a cabinet being set up.
  useEffect(() => {
    if (!deviceId) return undefined;
    let gone = false;
    const read = () => api.getDeviceHealth(deviceId)
      .then((h) => { if (!gone) setCameraRotation(h?.report?.cameras?.display_rotation || {}); })
      .catch(() => {});
    setCameraRotation({});
    read();
    const t = setInterval(read, 60000);
    return () => { gone = true; clearInterval(t); };
  }, [api, deviceId]);

  // -- load products once for EAN/name enrichment of detection events --
  useEffect(() => {
    api
      .listProducts()
      .then((list) => {
        const arr = Array.isArray(list) ? list : list?.data || [];
        const m = {};
        for (const p of arr) {
          const uuid = p.product_uuid || p.id;
          // The catalogue's field is product_name (the console read .name and showed bare ids);
          // the review and the cart events name products by their first 8 characters.
          const entry = { ean_code: p.ean_code, name: p.product_name || p.name || p.ean_code };
          if (uuid) { m[uuid] = entry; m[uuid.slice(0, 8)] = entry; }
        }
        setProductsByUuid(m);
      })
      .catch(() => {
        /* not fatal — detection panel just falls back to UUIDs */
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -- poll commands & live frame --
  const pollRef = useRef(null);
  useEffect(() => {
    if (!deviceId) return undefined;
    const tick = async () => {
      try {
        const data = await api.listDeviceCommands(deviceId, { limit: 200 });
        const cmds = data?.commands || [];
        setRecentCommands(cmds);
        // Infer session activity: most-recent successful session.start, only if
        // no later session.close has been issued OR acked. We deliberately
        // count *queued* (pending/delivered) close commands too: if the user
        // already clicked Stop, their intent is unambiguous, and the daemon
        // being offline shouldn't keep Start disabled forever. Acked errors
        // ("no active session") also terminate the active window.
        const startedAt = cmds
          .filter((c) => c.command_type === "session.start" && c.status === "done")
          .reduce((m, c) => Math.max(m, c.acked_at || 0), 0);
        const closedAt = cmds
          .filter((c) => c.command_type === "session.close")
          .reduce(
            (m, c) => Math.max(m, c.acked_at || c.created_at || 0),
            0,
          );
        setSessionActive(startedAt > closedAt);
      } catch (_e) {
        /* ignore polling errors */
      }
      try {
        const r = await api.getDevicePipelineLog(deviceId, { limit: 300 });
        setPipelineLog((r && r.entries) || []);
      } catch (_e) {
        /* ignore */
      }
    };
    tick();
    pollRef.current = window.setInterval(tick, 2000);
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
  }, [deviceId, api]);

  // -- live frames --
  // Two rates, deliberately. The SAS URL is minted rarely — it is valid for
  // two minutes and points at a blob the device overwrites — and the frame
  // itself is read straight from storage as fast as the device publishes.
  // That takes the Function App out of the per-frame path, which is what used
  // to cap the live view at a few frames a second. See lib/liveFrames.js.
  useEffect(() => {
    // Panes belong to one device. Without this a switch kept the previous
    // device's picture in any pane the new one had not refreshed yet, and a
    // camera that was not publishing showed somebody else's view.
    setCameras([]);
    if (!deviceId || !liveOn) return undefined;
    let cancelled = false;
    let renewAt = 0;
    let renewing = null;
    let renewTimer = null;
    let directFails = 0;
    let fallbackSince = 0;
    // Every start tries the direct path again.
    directFrameRef.current = true;
    // Pace from the end of each pass, and back off while nothing changes: the
    // Pi publishes about twice a second and the Jetson fifteen times, and
    // hammering the first at the second one's rate buys nothing. 60 ms is
    // just inside the Jetson's 67 ms publish interval — poll slower than the
    // device publishes and the browser shows fewer frames than were sent,
    // which is indistinguishable from the device being slow.
    const FAST_MS = 60;
    const SLOW_MS = 500;
    // A frame read that takes longer than this is abandoned and retried. With
    // no limit one request the network lost kept its camera frozen for good.
    const FETCH_TIMEOUT_MS = 4000;
    const sources = frameSourcesRef.current;
    const loops = new Map(); // camera -> timeout id of its next pass
    const inflight = new Set(); // AbortControllers to cancel on cleanup

    const commit = (camera, patch) =>
      setCameras((prev) => {
        const next = prev.some((p) => p.camera === camera)
          ? prev.map((p) => (p.camera === camera ? { ...p, ...patch } : p))
          : [...prev, { camera, url: null, overlay: null, ...patch }];
        return next.sort((a, b) => a.camera - b.camera);
      });

    // Ask the backend for fresh SAS URLs. In fallback mode this is also the
    // frame path, so it commits the URL and overlay it was given.
    const renew = async ({ display = false } = {}) => {
      const r = await api.getDeviceFrameUrl(deviceId);
      if (!r || cancelled) return;
      // `cameras` is the two-camera form; the flat url/overlay pair is what a
      // backend that predates it returns, and treating that as camera 0 keeps
      // the view working across a partial deploy.
      const list = Array.isArray(r.cameras) && r.cameras.length
        ? r.cameras
        : r.url
        ? [{ camera: 0, url: r.url, overlay: r.overlay ?? null }]
        : [];
      for (const c of list) {
        const src = sources.get(c.camera) || { camera: c.camera, lastTs: 0 };
        src.url = c.url;
        sources.set(c.camera, src);
        if (display) {
          src.frameAt = Date.now();
          src.checkedAt = Date.now();
          commit(c.camera, { url: `${c.url}&_=${Date.now()}`, overlay: c.overlay ?? null });
        }
        if (!loops.has(c.camera)) pumpCamera(c.camera);
      }
      // Renew well before the SAS expires; a 403 mid-stream would blank the
      // view for a whole pass while it recovered.
      renewAt = Date.now() + (r.ttl_seconds || 120) * 0.6 * 1000;
    };

    // Minting runs beside the frame reads, never in front of them. It is one
    // user delegation key and one property read per camera — seconds, not
    // milliseconds — and awaiting it inside the pump froze both panes every
    // 72 s while it ran.
    const ensureRenew = (opts) => {
      if (!renewing) {
        renewing = renew(opts)
          .catch(() => { renewAt = Date.now() + 3000; })
          .finally(() => { renewing = null; });
      }
      return renewing;
    };
    const renewLoop = async () => {
      if (cancelled) return;
      const direct = directFrameRef.current;
      // A direct read that failed for a network reason gets another chance
      // after a minute; one bad moment must not cost the session its frame rate.
      if (!direct && fallbackSince && Date.now() - fallbackSince > 60000) {
        directFrameRef.current = true;
        fallbackSince = 0;
        directFails = 0;
      }
      if (!direct || !sources.size || Date.now() >= renewAt) {
        await ensureRenew({ display: !direct });
      }
      if (!cancelled) renewTimer = window.setTimeout(renewLoop, direct ? 1000 : 700);
    };

    // One loop per camera. They used to share one pass, so the slower camera
    // set the pace for both and a stuck request stopped the whole view.
    const pumpCamera = (camera) => {
      let delay = FAST_MS;
      const pass = async () => {
        const started = performance.now();
        let changed = false;
        const src = sources.get(camera);
        if (src?.url && directFrameRef.current) {
          const ctl = new AbortController();
          inflight.add(ctl);
          const limit = window.setTimeout(() => ctl.abort(), FETCH_TIMEOUT_MS);
          try {
            const got = await fetchFrame(src, { signal: ctl.signal });
            src.checkedAt = Date.now();
            src.error = null;
            directFails = 0;
            if (got && !cancelled) {
              if (src.objectUrl) URL.revokeObjectURL(src.objectUrl);
              src.objectUrl = got.objectUrl;
              src.etag = got.etag;
              if (got.ts) src.lastTs = got.ts;
              src.frameAt = Date.now();
              changed = true;
              commit(camera, { url: got.objectUrl, overlay: got.overlay });
            }
          } catch (e) {
            if (cancelled) return;
            if (e.status === 403 || e.status === 404) {
              src.error = `storage answered ${e.status}`;
              renewAt = 0; // expired or not written yet: re-mint next pass
              ensureRenew({ display: false });
            } else if (e.name === "AbortError") {
              src.error = `no answer in ${FETCH_TIMEOUT_MS / 1000} s`;
            } else {
              src.error = e.message || "network error";
              // A CORS failure means direct reads are not available here at
              // all; retrying them every 80 ms would just be a fast way to
              // show nothing. Three in a row, not one: a single dropped
              // request is not CORS.
              directFails += 1;
              if (directFails >= 3) {
                directFrameRef.current = false;
                fallbackSince = Date.now();
              }
            }
          } finally {
            window.clearTimeout(limit);
            inflight.delete(ctl);
          }
        }
        if (cancelled) return;
        delay = changed ? FAST_MS : Math.min(Math.round(delay * 1.5), SLOW_MS);
        loops.set(camera, window.setTimeout(pass, nextDelay(delay, performance.now() - started)));
      };
      loops.set(camera, -1);
      pass();
    };

    renewLoop();
    return () => {
      cancelled = true;
      if (renewTimer) window.clearTimeout(renewTimer);
      for (const t of loops.values()) if (t > 0) window.clearTimeout(t);
      for (const ctl of inflight) ctl.abort();
      for (const src of sources.values()) {
        if (src.objectUrl) URL.revokeObjectURL(src.objectUrl);
      }
      sources.clear();
    };
  }, [deviceId, liveOn, api]);

  // How fresh each pane is, read once a second from the pump's bookkeeping.
  // A frozen pane and a quiet scene look the same; this says which, and
  // whose side the stall is on: the browser still gets answers (the cabinet
  // has not published that camera) or it gets none (the link or storage).
  const [liveClock, setLiveClock] = useState(0);
  useEffect(() => {
    if (!liveOn) return undefined;
    const t = window.setInterval(() => setLiveClock(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [liveOn]);
  const freshness = (camera) => {
    const src = frameSourcesRef.current.get(camera);
    if (!src?.frameAt || !liveClock) return null;
    const age = Math.max(0, (liveClock - src.frameAt) / 1000);
    if (age < 4) return null;
    const answering = src.checkedAt && liveClock - src.checkedAt < 4000;
    return {
      age,
      why: answering
        ? "the cabinet has not published this camera since"
        : `no answer from storage${src.error ? ` (${src.error})` : ""}`,
    };
  };

  // -- actions --
  const flash = (msg, tone = "info") => {
    setStatus({ msg, tone });
    window.setTimeout(() => setStatus(null), 4000);
  };

  const startSession = async () => {
    try {
      setError(null);
      // A new session starts with an empty cart on the device, so drop the
      // watermark or the view would keep hiding events from the last one.
      setBasketClearedAt(0);
      await api.sendDeviceCommand(deviceId, "session.start", {
        external_session_id: externalSession || null,
      });
      // Optimistic flip so the Start/Stop buttons enable/disable immediately
      // without waiting for the edge ack to come back through the 2 s poll.
      // The poll later reconciles with the authoritative command status.
      setSessionActive(true);
      setSessionStartedAt(Date.now() / 1000);
      flash("Start command queued", "success");
    } catch (e) {
      setError(e.message || "failed to send command");
    }
  };

  const closeSession = async () => {
    try {
      setError(null);
      await api.sendDeviceCommand(deviceId, "session.close", { reason: "manual" });
      setSessionActive(false);
      setSessionStartedAt(0);
      flash("Close command queued", "success");
    } catch (e) {
      setError(e.message || "failed to send command");
    }
  };

  // The device answers this command with a per-camera stillness measurement and
  // a warning when something moved. The panel used to discard both and say
  // "queued", which reads exactly the same whether the reference is clean or
  // was taken with the operator standing in the frame — and that failure only
  // shows up hours later, as products that never get recognised. So: queue it,
  // then wait for the device's own verdict and show the numbers.
  const captureBackground = async () => {
    const before = background?.ts || 0;
    try {
      await api.sendDeviceCommand(deviceId, "calibrate.background", { count: 12 });
      flash("Capturing — stay out of view of both cameras…", "info");
    } catch (e) {
      flash(e.message || "Failed to queue background capture", "error");
      return;
    }
    // The pipeline log is polled every 2 s; the capture itself takes a few.
    for (let i = 0; i < 15; i++) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 2000));
      const r = await api
        .getDevicePipelineLog(deviceId, { limit: 30 })
        .catch(() => null);
      const entry = (r?.entries || [])
        .slice()
        .reverse()
        .find((e) => (e.stage || e.event) === "background.captured" && (e.ts || 0) > before);
      if (!entry) continue;
      const motion = Object.entries(entry.motion || {})
        .map(([c, m]) => `cam${c} ${m}`)
        .join(", ");
      if (entry.warnings && entry.warnings.length) {
        flash(`Background REJECTED — ${entry.warnings.join("; ")} (${motion})`, "error");
      } else {
        flash(`Background captured, scene was still (${motion})`, "success");
      }
      return;
    }
    flash("Background capture sent, but the device never reported back", "error");
  };

  const uploadRecording = async () => {
    try {
      await api.sendDeviceCommand(deviceId, "recording.upload", { limit: 400 });
      flash("Recording upload queued — watch the log for recording.uploaded", "info");
    } catch (e) {
      setError(e.message || "failed to queue the recording upload");
    }
  };

  // No confirmation. This is a test bench and the operator presses it while
  // standing at the cabinet with a hand on the door; a dialog between them and
  // the lock is one more thing to dismiss before the unlock window starts
  // counting down.
  const openDoor = async () => {
    try {
      setError(null);
      await api.sendDeviceCommand(deviceId, "door.open", { reason: "test console" });
      flash("Open queued — watch the log for door.unlocked", "success");
    } catch (e) {
      setError(e.message || "failed to queue the door open");
    }
  };

  // One rehearsed purchase on a virtual cabinet: door.opened, a cart of one
  // line, door.closed, cart.settled — the real records through the real
  // fan-out, so the merchant's billing is what is under test. Nothing moves
  // because there is nothing to move.
  const sellOne = async () => {
    if (selling) return;
    setSelling(true);
    try {
      setError(null);
      const r = await api.sellFromEmulator(deviceId, {});
      const line = (r?.cart || [])[0];
      const gap = r?.delivery_schedule_s?.["cart.settled"] ?? 3;
      const what = line
        ? `${line.quantity} × ${line.product_name || line.ean_code}`
        : "an empty basket";
      flash(
        line
          ? `Sold ${what} — door.closed now, cart in ${gap}s`
          : "Door closed with an empty basket — cart.settled follows with no lines",
        "success"
      );
      // Shut here rather than waiting for the next poll, so the control goes
      // out of service the instant the visit is over instead of staying live
      // for up to three seconds with nothing left to sell.
      setVirtualDoor("closed");
      setLastSale({ what, gap });
    } catch (e) {
      setError(e.message || "the virtual cabinet could not complete the visit");
    } finally {
      setSelling(false);
    }
  };

  // No "Read state" button any more. Its whole job was to force a fresh
  // reading, and the device now pushes one the instant the door moves — the
  // heartbeat is woken on a transition instead of shipping on its idle
  // 60 s tick. A button to ask for something that already arrives by itself is
  // just a way to make the page look like it needs babysitting. The device
  // still answers the `door.state` command and still logs `door.reported`, so
  // nothing was removed from the API.

  const clearBasket = async () => {
    if (!window.confirm("Clear the basket? This empties the device's current cart.")) return;
    // Watermark first so the view clears immediately even if the device is
    // slow to pick the command up off the queue.
    setBasketClearedAt(Date.now() / 1000);
    try {
      await api.sendDeviceCommand(deviceId, "cart.clear", {});
      flash("Basket cleared", "success");
    } catch (err) {
      setError(err.message || "failed to clear basket");
    }
  };

  const enableLive = async () => {
    try {
      await api.sendDeviceCommand(deviceId, "live_stream.enable", {});
      setLiveOn(true);
      flash("Live view enabled", "success");
    } catch (e) {
      setError(e.message || "failed");
    }
  };

  const disableLive = async () => {
    try {
      await api.sendDeviceCommand(deviceId, "live_stream.disable", {});
      setLiveOn(false);
      setCameras([]);
      // The frame pump's cleanup runs off `liveOn` and is what revokes the
      // object URLs; clearing the sources here as well would race it.
    } catch (e) {
      setError(e.message || "failed");
    }
  };

  const applyConfig = async () => {
    try {
      await api.sendDeviceCommand(deviceId, "config.update", config);
      flash("Config update queued", "success");
    } catch (e) {
      setError(e.message || "failed");
    }
  };

  const toggleStatic = async (next) => {
    setStaticMode(next);
    try {
      // 1. Push immediate runtime override so detections start working now.
      await api.sendDeviceCommand(deviceId, "config.update", { static_mode: next });
      // 2. Persist on the device record so it survives restarts and stays in
      //    sync with the Devices page (camera_direction).
      try {
        await api.updateDevice(deviceId, {
          camera_direction: next ? "static" : "both",
        });
      } catch (_e) {
        /* non-fatal: command is already queued */
      }
      flash(`Static mode ${next ? "enabled" : "disabled"}`, "success");
    } catch (e) {
      setError(e.message || "failed");
    }
  };

  const selectedDevice = useMemo(
    () => devices.find((d) => (d.id || d.device_id) === deviceId),
    [devices, deviceId],
  );

  // A virtual cabinet has no runtime to drain the command queue: its visits
  // are run in the cloud. Everything else on this page still applies — the
  // pipeline log, the basket, the gates — it simply never publishes frames,
  // and its door control sells instead of unlocking.
  const isVirtual =
    (selectedDevice?.hardware || "").toLowerCase() === "emulator";

  // A virtual cabinet publishes no pipeline log, so the door reading this page
  // derives for a real device never arrives. Its state comes from the cloud
  // instead — and it is what decides whether there is a visit to sell from.
  const [virtualDoor, setVirtualDoor] = useState(null);
  useEffect(() => {
    if (!deviceId || !isVirtual) {
      setVirtualDoor(null);
      return undefined;
    }
    let cancelled = false;
    const read = async () => {
      try {
        const r = await api.getDeviceDoorState(deviceId);
        if (cancelled) return;
        const next = r?.state?.door || null;
        setVirtualDoor(next);
        // A fresh opening is a fresh visit: what the last one sold is history.
        if (next === "open") setLastSale(null);
      } catch {
        if (!cancelled) setVirtualDoor(null);
      }
    };
    read();
    const t = setInterval(read, 3000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [deviceId, isVirtual, api]);

  // The button is live only while a visit is open: selling with nothing open
  // produces a cart with no basket behind it, which the merchant can only log
  // and throw away.
  const canSell = isVirtual && virtualDoor === "open";

  // Pressing a button that does something on a server and then looks exactly
  // as it did before is how the same sale gets made twice. The control is
  // taken out of service the moment it is pressed, and says what it is doing.
  const [selling, setSelling] = useState(false);
  const [lastSale, setLastSale] = useState(null);

  // A device that isn't online cannot have a live session — keep the UI
  // consistent with reality so the operator can always re-issue Start once
  // the daemon comes back. Without this, a stale acked session.start from
  // a previous run keeps the Start button disabled indefinitely.
  const deviceOnline = selectedDevice?.online !== false;
  useEffect(() => {
    if (!deviceOnline) setSessionActive(false);
  }, [deviceOnline]);

  // Whenever the session flips off (either via the cloud-history poll or the
  // local Stop click), clear the "just started" timestamp so the chip stops
  // showing the soft blue "starting…" pill on the next Start.
  useEffect(() => {
    if (!sessionActive) setSessionStartedAt(0);
  }, [sessionActive]);

  // Seed the static checkbox from the selected device's camera_direction so
  // the UI reflects what's actually persisted (avoids drift between Devices
  // page and Test Console).
  useEffect(() => {
    if (selectedDevice) {
      setStaticMode(selectedDevice.camera_direction === "static");
    }
  }, [selectedDevice]);

  // Re-evaluate the "current detection" panel every poll tick even when
  // pipelineLog reference doesn't change, so stale entries fade away on their
  // own (without this, the panel would freeze on the last event forever once
  // the device stops emitting).
  const [detectionTick, setDetectionTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setDetectionTick((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, []);

  // Latest meaningful detection (or absence) from pipeline log — shown as a
  // running indicator so the operator can see live detection without parsing
  // raw events.
  const currentDetection = useMemo(() => {
    // Sticky window: once we see a positive detection we keep it on screen for
    // a few seconds so a single-frame hit isn't immediately overwritten by the
    // very next "fused.none" entry the model emits between strong predictions.
    // Episode mode decides ~3 s after the interaction ends, and the panel
    // polls every 2 s, so a 4 s window could expire before the operator ever
    // saw the result.
    const STICKY_MS = 9000;
    // Freshness window: pipeline log is server-persisted, so without this the
    // panel would keep showing the last event forever even after Stop / after
    // the operator leaves and comes back. Anything older than this is treated
    // as "no current activity". Set to 10 s to comfortably outlast the edge's
    // 2 s active-flush cadence plus our 2 s poll, leaving margin for jitter.
    const FRESH_MS = 10000;
    const nowSec = Date.now() / 1000;
    let lastPositive = null;
    let lastNegative = null;
    let lastAbstain = null;
    for (let i = pipelineLog.length - 1; i >= 0; i--) {
      const e = pipelineLog[i];
      // Stop walking once we're outside the freshness window — events are in
      // ascending ts order, so older entries can't become "current" later.
      if (typeof e.ts === "number" && nowSec - e.ts > FRESH_MS / 1000) break;
      const stage = e.stage || e.event;
      // Episode mode decides once per interaction rather than per frame, so
      // it emits episode.done/episode.abstain instead of infer.done/
      // infer.abstain. Without these branches the panel reports "no product
      // in view" while products are being recognised and added to the cart.
      if (!lastPositive && stage === "emit" && e.product_uuid) {
        lastPositive = {
          label: e.product_uuid,
          confidence: null,
          ts: e.ts,
          eventType: e.event_type,
        };
      }
      if (!lastPositive && stage === "episode.done" && e.cos >= (e.need_cos ?? 0)
          && e.margin >= (e.need_margin ?? 0)) {
        lastPositive = { label: e.uuid, confidence: e.cos, ts: e.ts };
      }
      if (!lastAbstain && stage === "episode.abstain") {
        lastAbstain = {
          reason: e.reason,
          top1_cos: e.cos,
          top1_margin: e.margin,
          raw_top1_label: e.uuid,
          top2_label: null,
          top2_cos: null,
          ts: e.ts,
          episode: true,
        };
      }
      if (!lastPositive && stage === "fused" && e.label) {
        lastPositive = { label: e.label, confidence: e.confidence, ts: e.ts };
      } else if (!lastPositive && stage === "infer.done" && e.per_camera) {
        const all = Object.values(e.per_camera).flat();
        if (all.length) {
          lastPositive = { label: all[0].label, confidence: all[0].conf, ts: e.ts };
        }
      }
      if (!lastAbstain && stage === "infer.abstain") {
        lastAbstain = {
          reason: e.reason,
          top1_cos: e.top1_cos,
          top1_margin: e.top1_margin,
          raw_top1_label: e.raw_top1_label,
          top2_label: e.top2_label,
          top2_cos: e.top2_cos,
          ts: e.ts,
        };
      }
      if (!lastNegative && (stage === "fused.none" || (stage === "infer.done" && e.per_camera && !Object.values(e.per_camera).flat().length))) {
        lastNegative = { label: null, ts: e.ts };
      }
      if (lastPositive && lastNegative && lastAbstain) break;
    }
    if (lastPositive && nowSec - lastPositive.ts <= STICKY_MS / 1000) {
      return { ...lastPositive, kind: "positive" };
    }
    // Prefer abstain detail over a plain "no detection" so the operator can
    // see WHY (low cosine, low margin, what the raw guess was). The runtime
    // always emits ``infer.abstain`` immediately before ``fused.none`` for
    // the same frame, so ``lastAbstain.ts`` is microseconds older than
    // ``lastNegative.ts``; treat them as the same cycle when within 1s.
    if (lastAbstain) {
      if (!lastNegative || Math.abs(lastNegative.ts - lastAbstain.ts) < 1.0
          || lastAbstain.ts >= lastNegative.ts) {
        return { ...lastAbstain, kind: "abstain" };
      }
    }
    if (lastNegative) return { ...lastNegative, kind: "negative" };
    return lastPositive ? { ...lastPositive, kind: "positive" } : null;
  }, [pipelineLog, detectionTick]);

  // Basket derived from the device's own emit stream: one entry per product,
  // incremented on product_taken and decremented on product_returned — the
  // same arithmetic the session cart applies, shown live so an operator can
  // watch the basket move while testing rather than reading raw log lines.
  //
  // Derived from the pipeline log rather than the cloud cart because the
  // console tracks device activity, not a session id. That means it reflects
  // the log's retained window, so it is a debugging view of what the device
  // decided, not the billing record.
  // What the device says it will do with a score. Published into the pipeline
  // log on session start and after every config change, because the console
  // reads that stream and nothing else — a gate that lives only in the
  // runtime's memory is a gate nobody can verify from here.
  // isReview: the cabinet decides by the opening review (handbook ch. 4), not the edge pipeline.
  const gates = useMemo(() => {
    for (let i = pipelineLog.length - 1; i >= 0; i--) {
      if ((pipelineLog[i].stage || pipelineLog[i].event) === "gates") return pipelineLog[i];
    }
    return null;
  }, [pipelineLog, detectionTick]);
  const isReview = (analysisMode || gates?.analysis_mode) === "review";

  // Door state and the live fraction behind it. The number matters as much as
  // the state: the thresholds are chosen by watching it move.
  const door = useMemo(() => {
    let state = null;
    let level = null;
    let source = null;
    let ts = 0;
    for (let i = pipelineLog.length - 1; i >= 0; i--) {
      const e = pipelineLog[i];
      const stage = e.stage || e.event;
      const isState = stage === "door.state" || stage === "door.reported";
      if (stage !== "door.sample" && !isState) continue;
      // The level only ever comes from a brightness sample...
      if (level === null && typeof e.level === "number") {
        level = e.level;
        ts = e.ts || 0;
      }
      // ...and the state never does. door.sample carries the *camera's*
      // opinion, which on a sensor-wired cabinet is the wrong answer dressed
      // as the right one — it reads "closed" off a dark frame whatever the
      // door is doing.
      if (state === null && isState && e.state) state = e.state;
      // Only the sensor path stamps a source; a brightness reading has none.
      if (source === null && e.source) source = e.source;
      if (level !== null && state !== null && source !== null) break;
    }
    // Transitions age out of the log window behind capture.timing, so a quiet
    // cabinet has none at all. `gates` is republished on a heartbeat and is
    // what keeps the chip honest between door movements.
    if (state === null && gates?.door_state) state = gates.door_state;
    if (source === null && gates?.door_signal) source = gates.door_signal;
    return { state, level, source, ts };
  }, [pipelineLog, detectionTick, gates]);

  // What the lock itself last did. `gates` carries the steady state (which
  // backend came up, whether the relay is at rest); the door.unlocked /
  // door.locked entries carry the last thing anyone asked it to do.
  const lockAction = useMemo(() => {
    for (let i = pipelineLog.length - 1; i >= 0; i--) {
      const e = pipelineLog[i];
      const stage = e.stage || e.event;
      if (stage === "door.unlocked" || stage === "door.locked") {
        return {
          action: stage === "door.unlocked" ? "unlocked" : "locked",
          source: e.source || null,
          mode: e.mode || null,
          pulseS: typeof e.pulse_s === "number" ? e.pulse_s : null,
          ts: e.ts || 0,
        };
      }
    }
    return null;
  }, [pipelineLog, detectionTick]);

  // The session outliving the door: after the door shuts the runtime keeps the
  // session open while episodes are still being scored, and says so. Without
  // this the chip reads "running" for a minute after the customer left and
  // the operator assumes it is stuck.
  const closing = useMemo(() => {
    const nowSec = Date.now() / 1000;
    for (let i = pipelineLog.length - 1; i >= 0; i--) {
      const e = pipelineLog[i];
      const stage = e.stage || e.event;
      if (stage === "session.auto_close" || stage === "session.auto_start") return null;
      if (stage === "session.close_deferred") {
        if (typeof e.ts === "number" && nowSec - e.ts > 20) return null;
        return { outstanding: e.outstanding, waited: e.waited_s };
      }
    }
    return null;
  }, [pipelineLog, detectionTick]);

  // The result of the last background capture, not just the fact one was
  // queued. A reference taken with somebody still in frame looks identical to
  // a good one until products stop being recognised hours later.
  const background = useMemo(() => {
    for (let i = pipelineLog.length - 1; i >= 0; i--) {
      const e = pipelineLog[i];
      const stage = e.stage || e.event;
      if (stage === "background.captured") {
        return {
          ok: !e.warnings || e.warnings.length === 0,
          warnings: e.warnings || [],
          motion: e.motion || {},
          cameras: e.cameras || [],
          ts: e.ts,
        };
      }
    }
    return null;
  }, [pipelineLog, detectionTick]);

  // Which products went to the cloud, and what came back. An escalation that
  // ended in an abstain is as informative as one that ended in a charge.
  const escalations = useMemo(() => {
    const out = [];
    for (let i = pipelineLog.length - 1; i >= 0 && out.length < 8; i--) {
      const e = pipelineLog[i];
      const stage = e.stage || e.event;
      if (stage === "escalate.done") {
        out.push({
          kind: "done", ts: e.ts, local: e.local, cloud: e.cloud,
          decision: e.cloud_decision, emitted: !!e.emit, reason: e.reason,
          abstained: e.abstained, tookMs: e.took_ms,
        });
      } else if (stage === "fallback.abstain") {
        out.push({
          kind: "done", ts: e.ts, local: e.local_label, cloud: e.cloud_label,
          decision: e.cloud_decision, emitted: false, reason: e.reason,
        });
      } else if (stage === "escalate.skip" || stage === "escalate.error") {
        out.push({ kind: "skip", ts: e.ts, reason: e.reason || "error", cos: e.cos });
      }
    }
    return out;
  }, [pipelineLog, detectionTick]);

  // Recorded sessions, from the log the console already reads. Each start /
  // stop / upload pair is one interaction; the panel is where an operator
  // decides which of them is worth pulling off the device.
  const recordings = useMemo(() => {
    const by = new Map();
    for (const e of pipelineLog) {
      const stage = e.stage || e.event;
      if (!String(stage).startsWith("recording.")) continue;
      const sid = e.session;
      if (stage === "recording.uploaded" || stage === "recording.autoupload") {
        // uploads carry no session id; attach to the most recent recording
        const last = [...by.values()].sort((a, b) => (b.ts || 0) - (a.ts || 0))[0];
        if (last) {
          last.uploaded = e.refs ?? e.files ?? 0;
          last.failed = e.failed ?? 0;
        }
        continue;
      }
      if (!sid) continue;
      const r = by.get(sid) || { session: sid, ts: e.ts, frames: 0, mb: 0 };
      r.ts = Math.max(r.ts || 0, e.ts || 0);
      if (stage === "recording.started") r.started = e.ts;
      if (stage === "recording.stopped") {
        r.stopped = e.ts;
        r.frames = e.frames ?? r.frames;
        r.mb = e.size_mb ?? r.mb;
        r.dropped = e.dropped_over_cap ?? 0;
      }
      by.set(sid, r);
    }
    return [...by.values()].sort((a, b) => (b.ts || 0) - (a.ts || 0)).slice(0, 8);
  }, [pipelineLog, detectionTick]);

  const basket = useMemo(() => {
    const byProduct = new Map();
    const events = [];
    for (const e of pipelineLog) {
      if ((e.stage || e.event) !== "emit") continue;
      if ((e.ts || 0) <= basketClearedAt) continue;
      const uuid = e.product_uuid || e.label;
      if (!uuid) continue;
      const delta = e.event_type === "product_returned" ? -1 : 1;
      // Where the name came from. ``source`` is the writer (the device, or the
      // cloud answering directly); ``origin: "cloud"`` is a local episode the
      // device abstained on and the cloud confirmed. Both are "the cloud had a
      // say", and that is the distinction worth seeing per product.
      const viaCloud = e.source === "azure_fallback" || e.origin === "cloud";
      // Which rung of the ladder charged: the fine gate, the closed-set
      // cascade among the stocked products, or the cloud. Worth seeing per
      // line — a cabinet where everything comes through the closed set is a
      // cabinet whose fine identification is not working.
      const viaClosedSet = e.origin === "closed_set";
      // The opening review on the device (docs/tasks/17): the product's own
      // track past the cameras decided, not an episode crop.
      const viaReview = e.origin === "review";
      const row = byProduct.get(uuid) || {
        uuid,
        ean: e.ean || null,
        qty: 0,
        taken: 0,
        returned: 0,
        lastTs: 0,
        local: 0,
        cloud: 0,
      };
      if (viaCloud) row.cloud += 1;
      else if (viaReview) row.review = (row.review || 0) + 1;
      else if (viaClosedSet) row.closedSet = (row.closedSet || 0) + 1;
      else row.local += 1;
      row.qty += delta;
      if (delta > 0) row.taken += 1;
      else row.returned += 1;
      if (e.ean) row.ean = e.ean;
      row.lastTs = Math.max(row.lastTs, e.ts || 0);
      byProduct.set(uuid, row);
      events.push({
        uuid, ean: e.ean, type: e.event_type, ts: e.ts,
        viaCloud, viaClosedSet, viaReview, camera: e.camera, agreement: e.agreement,
      });
    }
    const lines = [...byProduct.values()].sort((a, b) => b.lastTs - a.lastTs);
    return {
      lines,
      events: events.reverse().slice(0, 12),
      totalItems: lines.reduce((n, l) => n + Math.max(0, l.qty), 0),
    };
  }, [pipelineLog, detectionTick, basketClearedAt]);

  // Near misses: interactions the device analysed but did not count. This is
  // the question an operator actually has when the basket does not move —
  // "was it seen at all, and what stopped it?"
  const nearMisses = useMemo(() => {
    const out = [];
    for (let i = pipelineLog.length - 1; i >= 0 && out.length < 5; i--) {
      const e = pipelineLog[i];
      if ((e.stage || e.event) !== "episode.abstain") continue;
      out.push({
        uuid: e.uuid,
        cos: e.cos,
        margin: e.margin,
        reason: e.reason,
        ts: e.ts,
      });
    }
    return out;
  }, [pipelineLog, detectionTick]);

  // Ground-truth signal: is the *device* actually running detection right now?
  // The cloud-side ``sessionActive`` is just a reconciliation of cloud command
  // history (Start vs. Close) and can lag behind reality — e.g. the runtime
  // crashed, the daemon ran a close it queued during a previous boot, or the
  // operator restarted the Pi. The pipeline log is the only signal that the
  // device is actually inferring on frames.
  //
  // We count BOTH ``infer.*`` and ``motion`` events as proof of life because
  // inference is motion-gated on the edge: when nothing moves there are no
  // ``infer.*`` events, but the motion detector still ticks. Without this the
  // chip would flicker yellow whenever the operator holds a product still.
  const deviceInferring = useMemo(() => {
    const FRESH_MS = 12000;
    const nowSec = Date.now() / 1000;
    for (let i = pipelineLog.length - 1; i >= 0; i--) {
      const e = pipelineLog[i];
      if (typeof e.ts !== "number") continue;
      if (nowSec - e.ts > FRESH_MS / 1000) return false;
      const stage = e.stage || e.event;
      if (
        stage === "infer.done" ||
        stage === "infer.abstain" ||
        stage === "episode.done" ||
        stage === "episode.abstain" ||
        stage === "fused" ||
        stage === "fused.none" ||
        stage === "motion"
      ) {
        return true;
      }
    }
    return false;
  }, [pipelineLog, detectionTick]);

  return (
    <div className="min-w-0 space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Test Console</h1>
          <p className="text-sm text-slate-500">
            Take a product, watch what the cabinet decided and why. The basket is
            the answer; everything below it is the evidence.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
        <RecordingForTraining deviceId={deviceId} gates={gates}
          send={(keys) => api.sendDeviceCommand(deviceId, "config.update", keys)} />
        <div className={fixedDeviceId ? "hidden" : ""}>
          <label className="block text-xs font-medium text-slate-500">Device</label>
          <select
            value={deviceId}
            onChange={(e) => setDeviceId(e.target.value)}
            className="rounded border border-slate-300 px-2 py-1 text-sm"
          >
            {devices.map((d) => {
              const id = d.id || d.device_id;
              return (
                <option key={id} value={id}>
                  {(d.name || id)} {d.online ? "● online" : "○ offline"}
                </option>
              );
            })}
          </select>
        </div>
        </div>
      </div>

      {/* Fixed, not in flow. These used to sit above the grid, so every flash
          pushed the entire page down for four seconds and then let it snap
          back — the chips, the camera and the basket all moved under the
          cursor. A message about something that already happened has no
          business relaying out the page it is reporting on. */}
      <div className="pointer-events-none fixed bottom-4 right-4 z-50 flex w-80 max-w-[90vw] flex-col gap-2">
        {error && (
          <div className="pointer-events-auto shadow-lg">
            <Notice tone="error">{error}</Notice>
          </div>
        )}
        {status && (
          <div className="pointer-events-auto shadow-lg">
            <Notice tone={status.tone}>{status.msg}</Notice>
          </div>
        )}
      </div>

      {/* Everything that must be true before a test means anything, on one
          line. Each of these has been wrong at least once while somebody ran a
          test and drew a conclusion from it. */}
      <div className="flex flex-wrap items-stretch gap-2">
        {/* Built as a tile rather than loose buttons: the status line is a row
            of bordered cards, and three floating buttons hanging off the end of
            it read as something that landed there by accident. Same border,
            radius, padding and label treatment as Chip — it just holds controls
            instead of a reading. It leads the row because it is the one thing
            here you act with, and it does not grow: the readings share the
            remaining width between them. */}
        <div className="min-w-0 shrink-0 rounded-lg border border-slate-200 bg-white px-3 py-2">
          <div className="text-[10px] uppercase tracking-wide text-slate-500">Door</div>
          {/* Open is the only control here. A Lock button was tried and taken
              out again: this lock re-latches by itself the moment the door
              shuts, so in normal use there is nothing for it to do, and the one
              case where it helps — aborting an unlock nobody meant to start —
              is rare enough not to earn a permanent place on the status line.
              The capability itself stays: `door.lock` over the command queue,
              /local/door/lock on the device, and the cancel that shutdown
              depends on. */}
          <div className="mt-1">
            <button
              onClick={isVirtual ? sellOne : openDoor}
              disabled={!deviceId || (isVirtual && (!canSell || selling))}
              title={
                isVirtual
                  ? canSell
                    ? "Take one product out of the open visit and shut the door. The cart follows three seconds later, on the basket the merchant opened with."
                    : "Waiting for a door.open — this cabinet sells into the visit the merchant started, so the cart lands on their basket."
                  : "Release the bolt. The signal is held until the door opens or the window expires."
              }
              className="rounded-md bg-emerald-600 px-3 py-1 text-xs font-medium text-white transition-colors hover:bg-emerald-700 active:bg-emerald-800 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500"
            >
              {isVirtual ? (selling ? "Selling…" : "Sell one") : "Open"}
            </button>
            {isVirtual ? (
              <div className="mt-1 text-[10px] leading-tight text-slate-500">
                {selling ? (
                  <span className="text-emerald-700">selling…</span>
                ) : lastSale ? (
                  <span className="text-emerald-700">
                    sold {lastSale.what} · cart in {lastSale.gap}s
                  </span>
                ) : canSell ? (
                  "door open — waiting for a hand"
                ) : (
                  "waiting for door.open"
                )}
              </div>
            ) : null}
          </div>
        </div>

        <Chip
          label="Door"
          tone={door.state === "open" ? "good" : door.state ? "idle" : "unknown"}
          value={door.state ? door.state.toUpperCase() : "no reading"}
          detail={
            // Say which signal this is. The sensor answers directly; brightness
            // infers it from the door camera and cannot tell a dead camera from
            // a cabinet nobody opens.
            (gates?.door_signal || door.source) === "sensor"
              ? `magnetic contact${gates?.lock_backend ? ` · ${gates.lock_backend}` : ""}${
                  gates?.lock_locked === false ? " · bolt released" : ""
                }`
              : door.level !== null
                ? `camera light ${(door.level * 100).toFixed(1)}% · opens above ${(
                    (gates?.door_open_level ?? 0.12) * 100
                  ).toFixed(0)}%`
                : "no sensor wired · start live view for a camera reading"
          }
        />
        <Chip
          label="Lock"
          tone={
            !gates ? "unknown"
              : gates.lock_backend === "simulated" ? "off"
              : gates.lock_locked === false ? "warn"
              : "good"
          }
          value={
            !gates ? "unknown"
              : gates.lock_backend === "simulated" ? "simulated"
              : gates.lock_locked === false ? "RELEASED"
              : "locked"
          }
          detail={
            !gates
              ? ""
              : gates.lock_backend === "simulated"
                ? "no GPIO on this device — the door path runs, nothing moves"
                : lockAction
                  ? `last ${lockAction.action}${lockAction.source ? ` by ${lockAction.source}` : ""}`
                  : "at rest since boot"
          }
        />
        <Chip
          label="Session"
          tone={sessionActive ? (closing ? "warn" : deviceInferring ? "good" : "warn") : "idle"}
          value={
            sessionActive
              ? closing
                ? "finishing"
                : deviceInferring
                  ? "running"
                  : "no frames"
              : "idle"
          }
          detail={
            gates?.analysis_mode === "cloud"
              ? "capture only · recordings go to the cloud (test mode)"
              : gates?.analysis_mode === "review"
                ? "decided on the device per opening · cloud is reserve"
                : closing
                ? `door shut · scoring ${closing.outstanding} episode${closing.outstanding === 1 ? "" : "s"} (${Math.round(closing.waited ?? 0)} s)`
                : gates?.door_auto_session
                  ? "started by the door"
                  : "started by hand"
          }
        />
        <Chip
          label="Cloud arbiter"
          tone={gates ? (gates.cloud_fallback_enabled ? "good" : "off") : "unknown"}
          value={
            gates ? (gates.cloud_fallback_enabled ? "armed" : "off") : "unknown"
          }
          detail={
            gates?.cloud_fallback_enabled
              ? "reserve only: asked when the device is unsure"
              : "off: the device decides alone"
          }
        />
        <Chip
          label="Projection"
          tone={gates ? (gates.projection_loaded ? "good" : "off") : "unknown"}
          value={gates ? (gates.projection_loaded ? "loaded" : "absent") : "unknown"}
          detail={
            gates?.projection_loaded
              ? "identity judged in the projected space"
              : "raw margin only"
          }
        />
        <Chip
          label="Background"
          tone={background ? (background.ok ? "good" : "bad") : "unknown"}
          value={background ? (background.ok ? "clean" : "suspect") : "not captured here"}
          detail={
            background
              ? Object.entries(background.motion || {})
                  .map(([c, m]) => `cam${c} ${m}`)
                  .join(" · ") || "captured"
              : "capture one with the cabinet empty"
          }
        />
        <Chip
          label="Stock"
          tone={
            !gates ? "unknown"
              : gates.stock_source === "cloud_planogram" ? "good"
              : gates.stock_source === "env" ? "idle"
              : "warn"
          }
          value={
            !gates ? "unknown"
              : gates.stock_source === "unrestricted" ? "any product"
              : `${gates.stock_count ?? "?"} product${gates.stock_count === 1 ? "" : "s"}`
          }
          detail={
            !gates ? "" :
            gates.stock_source === "cloud_planogram"
              ? "from the planogram — nothing else can be charged"
              : gates.stock_source === "env"
                ? "from the device's own setting"
                : "no shelf list: the whole catalogue may win"
          }
        />
        <Chip
          label="Gallery"
          tone={gates?.model_version ? "good" : "unknown"}
          value={gates?.model_version || "—"}
          detail="the catalogue this device is scoring against"
        />

      </div>

      <div className="grid min-w-0 gap-4 md:grid-cols-2">
        <Section
          className="order-2"
          title="Products taken"
          right={
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">
                {basket.totalItems} item{basket.totalItems === 1 ? "" : "s"}
              </span>
              <button
                onClick={clearBasket}
                disabled={!deviceId}
                title="Empty the device's current cart and reset this view"
                className="rounded border border-rose-300 px-2 py-0.5 text-xs text-rose-700 hover:bg-rose-50 disabled:opacity-50"
              >
                Clear
              </button>
            </div>
          }
        >
          <p className="mb-2 text-xs text-slate-500">
            What the device decided: a take adds one, a return removes one.{" "}
            <span className="rounded bg-slate-100 px-1 font-medium">local</span>{" "}
            means the device named the product on its own;{" "}
            <span className="rounded bg-indigo-100 px-1 font-medium text-indigo-800">cloud</span>{" "}
            means it abstained and the cloud confirmed the same product — the
            cloud can only agree, never contradict. Built from the retained
            pipeline window, so this is the debugging view, not the billing
            record. <strong>Clear</strong> empties the device's cart too.
          </p>

          {basket.lines.length === 0 ? (
            <div className="rounded border border-slate-200 p-3 text-xs text-slate-500">
              Basket is empty — no products taken yet in the retained window.
            </div>
          ) : (
            <div className="min-w-0 overflow-auto rounded border border-slate-200">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-2 py-1 text-left font-medium">EAN</th>
                    <th className="px-2 py-1 text-left font-medium">Product</th>
                    <th className="px-2 py-1 text-left font-medium">Named by</th>
                    <th className="px-2 py-1 text-right font-medium">Qty</th>
                    <th className="px-2 py-1 text-right font-medium">Taken</th>
                    <th className="px-2 py-1 text-right font-medium">Returned</th>
                    <th className="px-2 py-1 text-right font-medium">Last</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {basket.lines.map((l) => {
                    const p = productsByUuid[l.uuid] || {};
                    return (
                      <tr key={l.uuid} className={l.qty <= 0 ? "text-slate-400" : ""}>
                        <td className="px-2 py-1 font-mono">{l.ean || "—"}</td>
                        <td className="px-2 py-1">
                          {p.name || <span className="font-mono">{l.uuid.slice(0, 8)}</span>}
                        </td>
                        <td className="px-2 py-1">
                          {l.local > 0 && (
                            <span className="mr-1 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-700">
                              local{l.local > 1 ? ` ×${l.local}` : ""}
                            </span>
                          )}
                          {l.review > 0 && (
                            <span className="mr-1 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] text-emerald-800"
                                  title="Decided by the device's opening review: the product's own pass in the customer's hand">
                              review{l.review > 1 ? ` ×${l.review}` : ""}
                            </span>
                          )}
                          {l.closedSet > 0 && (
                            <span className="mr-1 rounded bg-violet-100 px-1.5 py-0.5 text-[10px] text-violet-800"
                                  title="Chosen among the products the planogram says the cabinet holds">
                              closed set{l.closedSet > 1 ? ` ×${l.closedSet}` : ""}
                            </span>
                          )}
                          {l.cloud > 0 && (
                            <span className="rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] text-indigo-800">
                              cloud{l.cloud > 1 ? ` ×${l.cloud}` : ""}
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-1 text-right font-semibold tabular-nums">
                          {l.qty}
                        </td>
                        <td className="px-2 py-1 text-right tabular-nums text-emerald-700">
                          +{l.taken}
                        </td>
                        <td className="px-2 py-1 text-right tabular-nums text-sky-700">
                          {l.returned ? `-${l.returned}` : "0"}
                        </td>
                        <td className="px-2 py-1 text-right font-mono text-slate-500">
                          {l.lastTs
                            ? new Date(l.lastTs * 1000).toLocaleTimeString()
                            : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {basket.events.length > 0 && (
            <div className="mt-3">
              <div className="mb-1 text-xs font-medium text-slate-600">Recent events</div>
              <ul className="divide-y rounded border border-slate-200 text-xs">
                {basket.events.map((e, i) => (
                  <li key={`${e.ts}-${i}`} className="flex items-center gap-2 px-2 py-1">
                    <span className="font-mono text-[11px] text-slate-500">
                      {e.ts ? new Date(e.ts * 1000).toLocaleTimeString() : "—"}
                    </span>
                    <span
                      className={`rounded px-1.5 py-0.5 text-[10px] ${
                        e.type === "product_returned"
                          ? "bg-sky-100 text-sky-700"
                          : "bg-emerald-100 text-emerald-700"
                      }`}
                    >
                      {e.type === "product_returned" ? "returned −1" : "taken +1"}
                    </span>
                    <span className="font-mono text-[11px]">{e.ean || e.uuid.slice(0, 8)}</span>
                    <span
                      className={`rounded px-1.5 py-0.5 text-[10px] ${
                        e.viaCloud
                          ? "bg-indigo-100 text-indigo-800"
                          : e.viaReview
                            ? "bg-emerald-100 text-emerald-800"
                            : e.viaClosedSet
                              ? "bg-violet-100 text-violet-800"
                              : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      {e.viaCloud ? "cloud" : e.viaReview ? "review" : e.viaClosedSet ? "closed set" : "local"}
                    </span>
                    {typeof e.camera === "number" && (
                      <span className="text-[10px] text-slate-400">cam{e.camera}</span>
                    )}
                    {e.agreement && (
                      <span className="text-[10px] text-slate-400">{e.agreement}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {nearMisses.length > 0 && (
            <div className="mt-3">
              {/* The question when the basket does not move is "was it seen at
                  all?" — these are interactions the device analysed and
                  rejected, with the number that caused the rejection. */}
              <div className="mb-1 text-xs font-medium text-slate-600">
                Seen but not counted
              </div>
              <ul className="divide-y rounded border border-amber-200 bg-amber-50/40 text-xs">
                {nearMisses.map((m, i) => {
                  const p = productsByUuid[m.uuid] || {};
                  return (
                    <li key={`${m.ts}-${i}`} className="flex flex-wrap items-center gap-2 px-2 py-1">
                      <span className="font-mono text-[11px] text-slate-500">
                        {m.ts ? new Date(m.ts * 1000).toLocaleTimeString() : "—"}
                      </span>
                      <span className="font-medium">
                        {p.name || (m.uuid || "").slice(0, 8)}
                      </span>
                      <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] text-amber-800">
                        {m.reason}
                      </span>
                      <span className="font-mono text-[11px] text-slate-600">
                        cos {Number(m.cos ?? 0).toFixed(3)} · margin{" "}
                        {Number(m.margin ?? 0).toFixed(3)}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {escalations.length > 0 && (
            <div className="mt-3">
              {/* What the cloud was asked and what it answered. An escalation
                  that ended in an abstain is as informative as one that ended
                  in a charge: it says the two opinions disagreed, which is
                  precisely when charging anybody would be a coin flip. */}
              <div className="mb-1 text-xs font-medium text-slate-600">
                Sent to the cloud
              </div>
              <ul className="divide-y rounded border border-indigo-200 bg-indigo-50/40 text-xs">
                {escalations.map((x, i) => (
                  <li key={`${x.ts}-${i}`} className="flex flex-wrap items-center gap-2 px-2 py-1">
                    <span className="font-mono text-[11px] text-slate-500">
                      {x.ts ? new Date(x.ts * 1000).toLocaleTimeString() : "—"}
                    </span>
                    {x.kind === "skip" ? (
                      <>
                        <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] text-slate-700">
                          not sent
                        </span>
                        <span className="text-slate-600">{x.reason}</span>
                        {typeof x.cos === "number" && (
                          <span className="font-mono text-[11px] text-slate-500">
                            cos {x.cos.toFixed(3)}
                          </span>
                        )}
                      </>
                    ) : (
                      <>
                        <span
                          className={`rounded px-1.5 py-0.5 text-[10px] ${
                            x.emitted
                              ? "bg-emerald-100 text-emerald-800"
                              : "bg-slate-200 text-slate-700"
                          }`}
                        >
                          {x.emitted ? "charged" : "no charge"}
                        </span>
                        <span className="text-slate-600">
                          device said{" "}
                          <strong>
                            {productsByUuid[x.local]?.name || (x.local || "—")}
                          </strong>
                          , cloud said{" "}
                          <strong>{x.cloud || "—"}</strong>
                        </span>
                        <span className="font-mono text-[11px] text-slate-500">
                          {x.decision}
                          {x.abstained ? ` · after ${x.abstained}` : ""}
                          {x.tookMs ? ` · ${x.tookMs}ms` : ""}
                        </span>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Section>

        {/* Edge-mode controls: inert on a cabinet that runs the opening review
            (docs/tasks/35 review: session.start without a door, static_mode,
            episode gates and their stages never happen there). */}
        {!isReview && (
        <Section
          className="order-3"
          title="Detection control"
          right={
            <span
              className={`rounded-full px-2 py-0.5 text-xs ${
                deviceInferring
                  ? "bg-emerald-100 text-emerald-700"
                  : sessionActive
                    ? Date.now() / 1000 - sessionStartedAt < 10
                      ? "bg-sky-100 text-sky-700"
                      : "bg-amber-100 text-amber-800"
                    : "bg-slate-100 text-slate-600"
              }`}
              title={
                deviceInferring
                  ? "Device is scanning and reporting events"
                  : sessionActive
                    ? Date.now() / 1000 - sessionStartedAt < 10
                      ? "Waiting for the device to begin…"
                      : "Cloud session is open but device has gone silent — try Stop then Start"
                    : "No active session"
              }
            >
              {deviceInferring
                ? "scanning"
                : sessionActive
                  ? Date.now() / 1000 - sessionStartedAt < 10
                    ? "starting…"
                    : "stalled · try Stop+Start"
                  : "idle"}
            </span>
          }
        >
          <div className="space-y-2">
            <input
              className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
              placeholder="external session id (optional, e.g. POS cart-1234)"
              value={externalSession}
              onChange={(e) => setExternalSession(e.target.value)}
            />
            <div className="flex flex-wrap items-center gap-2">
              {/* Buttons reflect *device* truth, not cloud command history,
                  because the cloud heuristic can get stuck in a "ghost session"
                  state (start acked, close never reached us, etc.). Start is
                  disabled only when the device is actually emitting inference
                  events; Stop is enabled whenever either cloud or device thinks
                  there's something running, so the operator can always recover.
                  The daemon handles duplicate start/close idempotently. */}
              <button
                onClick={startSession}
                disabled={!deviceId || deviceInferring}
                className="btn-primary text-sm disabled:cursor-not-allowed disabled:opacity-50"
                title={deviceInferring ? "Device is already running detection" : "Start a new detection session"}
              >
                Start detection
              </button>
              <button
                onClick={closeSession}
                disabled={!deviceId || (!sessionActive && !deviceInferring)}
                className="rounded border border-slate-300 px-3 py-1 text-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                title={
                  deviceInferring
                    ? "Stop the running session"
                    : sessionActive
                      ? "Clear the cloud-side session (device is already idle)"
                      : "No active session to stop"
                }
              >
                Stop detection
              </button>
              <label className="flex items-center gap-1 text-xs text-slate-700">
                <input
                  type="checkbox"
                  checked={staticMode}
                  onChange={(e) => toggleStatic(e.target.checked)}
                  disabled={!deviceId}
                />
                static (single-camera POS)
              </label>
            </div>
            {selectedDevice && (
              <p className="text-xs text-slate-500">
                Current model:{" "}
                <code>
                  {selectedDevice.reported_model_id ||
                    selectedDevice.current_model_id ||
                    selectedDevice.desired_model_id ||
                    selectedDevice.reported_model_version ||
                    selectedDevice.model_version ||
                    "—"}
                </code>
                {" · camera_direction: "}
                <code>{selectedDevice.camera_direction || "—"}</code>
              </p>
            )}
            <div className="mt-2 rounded border border-slate-200 bg-slate-50 p-2 text-xs">
              <div className="mb-1 font-medium text-slate-600">Current detection</div>
              {!currentDetection ? (
                <span className="text-slate-500">
                  {deviceInferring
                    ? "scanning — no product in view"
                    : sessionActive
                      ? Date.now() / 1000 - sessionStartedAt < 10
                        ? "starting session on the device…"
                        : "device is not emitting events — try Stop then Start"
                      : "session idle — press Start detection"}
                </span>
              ) : currentDetection.kind === "positive" && currentDetection.label ? (() => {
                  const p = productsByUuid[currentDetection.label] || {};
                  return (
                    <div className="space-y-0.5">
                      <div>
                        <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-800">
                          detected
                        </span>{" "}
                        <strong>{p.name || currentDetection.label}</strong>
                        {typeof currentDetection.confidence === "number" && (
                          <span className="ml-2 text-slate-600">
                            conf={(currentDetection.confidence * 100).toFixed(1)}%
                          </span>
                        )}
                        <span className="ml-2 text-slate-400">
                          {currentDetection.ts ? new Date(currentDetection.ts * 1000).toLocaleTimeString() : ""}
                        </span>
                      </div>
                      <div className="text-slate-600">
                        EAN: <code>{p.ean_code || "—"}</code>{" · "}
                        UUID: <code className="text-[10px]">{currentDetection.label}</code>
                      </div>
                    </div>
                  );
                })()
              : currentDetection.kind === "abstain" ? (() => {
                  const p1 = productsByUuid[currentDetection.raw_top1_label] || {};
                  const p2 = productsByUuid[currentDetection.top2_label] || {};
                  return (
                    <div className="space-y-0.5">
                      <div>
                        <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
                          no match
                        </span>{" "}
                        <span className="text-slate-600">
                          reason: <code>{currentDetection.reason || "—"}</code>
                        </span>
                        <span className="ml-2 text-slate-400">
                          {currentDetection.ts ? new Date(currentDetection.ts * 1000).toLocaleTimeString() : ""}
                        </span>
                      </div>
                      <div className="text-slate-600">
                        best guess: <strong>{p1.name || currentDetection.raw_top1_label || "—"}</strong>
                        {p1.ean_code && <> · EAN <code>{p1.ean_code}</code></>}
                      </div>
                      <div className="text-slate-500">
                        cos={typeof currentDetection.top1_cos === "number" ? currentDetection.top1_cos.toFixed(3) : "—"}
                        {" · "}
                        margin={typeof currentDetection.top1_margin === "number" ? currentDetection.top1_margin.toFixed(3) : "—"}
                      </div>
                      {currentDetection.top2_label && (
                        <div className="text-slate-500">
                          runner-up: {p2.name || currentDetection.top2_label}
                          {p2.ean_code && <> · EAN <code>{p2.ean_code}</code></>}
                          {" · "}
                          cos={typeof currentDetection.top2_cos === "number" ? currentDetection.top2_cos.toFixed(3) : "—"}
                        </div>
                      )}
                    </div>
                  );
                })()
              : (
                <span className="text-slate-500">no product in view</span>
              )}
            </div>
          </div>
        </Section>
        )}

        <Section
          className="order-1"
          title="Live view"
          right={
            <div className="flex items-center gap-2">
              <button
                onClick={captureBackground}
                disabled={!deviceId}
                title="Record the empty scene. Frames matching it are skipped, so static background is never reported as a product."
                className="rounded border border-slate-300 px-2 py-0.5 text-xs hover:bg-slate-50 disabled:opacity-50"
              >
                Capture background
              </button>
              {liveOn ? (
                <button
                  onClick={disableLive}
                  className="rounded border border-slate-300 px-2 py-0.5 text-xs hover:bg-slate-50"
                >
                  Stop live
                </button>
              ) : (
                <button
                  onClick={enableLive}
                  disabled={!deviceId}
                  className="btn-primary text-xs"
                >
                  Start live
                </button>
              )}
            </div>
          }
        >
          {liveOn && cameras.length ? (
            // Two cameras side by side once the cabinet has two. Side by side
            // rather than switchable: judging whether a product was taken or
            // put back means comparing the two viewpoints at the same instant,
            // and a toggle makes that impossible.
            <div
              className={`grid gap-2 ${cameras.length > 1 ? "sm:grid-cols-2" : "grid-cols-1"}`}
            >
              {cameras.map((c) => (
                <CameraPane
                  key={c.camera}
                  cameraId={c.camera}
                  frameUrl={c.url}
                  overlay={c.overlay}
                  names={productsByUuid}
                  label={cameraLabels[c.camera] || `camera ${c.camera}`}
                  rotation={cameraRotation[c.camera] ?? cameraRotation[String(c.camera)] ?? 0}
                  stale={freshness(c.camera)}
                  onExpand={() => setBigCamera(c.camera)}
                  calibration={calibration?.cameras?.[String(c.camera)] || null}
                  corrected={!!correctedView[c.camera]}
                  onToggleCorrected={() => toggleCorrected(c.camera)}
                />
              ))}
              <ReviewNow cameras={cameras} names={productsByUuid} labels={cameraLabels} />
            </div>
          ) : (
            <div className="relative aspect-video w-full overflow-hidden rounded border border-slate-200 bg-slate-100 flex items-center justify-center">
              <span className="text-sm text-slate-500">
                {liveOn ? "waiting for first frame…" : "live view is off"}
              </span>
            </div>
          )}
          {liveOn && bigCamera !== null && cameras.some((c) => c.camera === bigCamera) ? (
            <BigCameraView
              cameras={cameras}
              camera={bigCamera}
              onCamera={setBigCamera}
              onClose={() => setBigCamera(null)}
              names={productsByUuid}
              labels={cameraLabels}
              rotation={cameraRotation}
              freshness={freshness}
              calibration={calibration}
              correctedView={correctedView}
              onToggleCorrected={toggleCorrected}
              gates={gates}
              send={(keys) => api.sendDeviceCommand(deviceId, "config.update", keys)}
            />
          ) : null}
          <p className="mt-2 text-xs text-slate-500">
            Frames are read straight from storage, so the view runs at whatever the
            device publishes (AINAOPEN_LIVE_FPS — 2 on a Pi 5, 15 on a Jetson) rather
            than at the rate the API can mint URLs. The box always belongs to the frame
            it is drawn on: image and overlay arrive in one response, and a frame that
            lands out of order is dropped rather than shown. Every camera the device has
            appears here; each pane carries its own overlay, because the two cameras see
            the same pickup differently and that difference is the point. Each camera
            is read on its own, so a slow one does not hold the other back; a pane
            that has had no new frame for 4 s says so and whose side the stall is
            on. The expand button opens one camera full screen, where the texts
            and the boxes can be switched off to see the picture itself.
          </p>
        </Section>

        <Section
          className="order-5 md:col-span-2"
          title="Recorded sessions"
          right={
            <button
              onClick={uploadRecording}
              disabled={!deviceId}
              className="rounded border border-slate-300 px-2 py-0.5 text-xs hover:bg-slate-50 disabled:opacity-50"
            >
              Upload latest to cloud
            </button>
          }
        >
          <p className="mb-2 text-xs text-slate-500">
            Every frame of every camera from the moment the cabinet opens until
            it shuts — not the frames an episode happened to keep. A customer
            takes a product in two to five seconds, so this is the only record
            with enough frames in it to explain what happened. The device keeps{" "}
            <strong>one</strong> recording: the next opening clears it, so pull
            anything you want to keep before the next interaction.
          </p>

          <label className="mb-3 flex items-start gap-2 text-xs">
            <input
              type="checkbox"
              checked={
                config.session_recording_cloud_stream ??
                !!gates?.session_recording_cloud_stream
              }
              onChange={(e) =>
                setConfig((c) => ({
                  ...c,
                  session_recording_cloud_stream: e.target.checked,
                }))
              }
              className="mt-0.5"
            />
            <span>
              <strong>Stream every recording to the cloud</strong> — upload
              automatically when the cabinet shuts, instead of waiting to be
              asked.
              <span className="block text-[10px] text-slate-500">
                Removes the race: the window in which a recording can be
                retrieved is the window before the next one starts. Costs a few
                hundred MB per interaction, so leave it off outside testing.
                Push it with the Gates panel's button - or use "Record for
                training" at the top, which switches only this.
              </span>
            </span>
          </label>

          {recordings.length === 0 ? (
            <div className="rounded border border-slate-200 p-3 text-xs text-slate-500">
              Nothing recorded in the retained window.
            </div>
          ) : (
            <div className="min-w-0 overflow-auto rounded border border-slate-200">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-2 py-1 text-left font-medium">Started</th>
                    <th className="px-2 py-1 text-left font-medium">Session</th>
                    <th className="px-2 py-1 text-right font-medium">Seconds</th>
                    <th className="px-2 py-1 text-right font-medium">Frames</th>
                    <th className="px-2 py-1 text-right font-medium">MB</th>
                    <th className="px-2 py-1 text-left font-medium">In cloud</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {recordings.map((r) => {
                    const secs =
                      r.started && r.stopped ? (r.stopped - r.started).toFixed(1) : "—";
                    return (
                      <tr key={r.session}>
                        <td className="px-2 py-1 font-mono text-[11px] text-slate-500">
                          {r.started
                            ? new Date(r.started * 1000).toLocaleTimeString()
                            : "—"}
                        </td>
                        <td className="px-2 py-1 font-mono text-[11px]">
                          {String(r.session).slice(0, 8)}
                        </td>
                        <td className="px-2 py-1 text-right tabular-nums">{secs}</td>
                        <td
                          className={`px-2 py-1 text-right tabular-nums ${
                            r.frames && r.frames < 20 ? "text-amber-700 font-semibold" : ""
                          }`}
                          title={
                            r.frames && r.frames < 20
                              ? "Too few frames to explain an interaction"
                              : ""
                          }
                        >
                          {r.frames ?? "—"}
                        </td>
                        <td className="px-2 py-1 text-right tabular-nums">
                          {r.mb ? Number(r.mb).toFixed(1) : "—"}
                        </td>
                        <td className="px-2 py-1">
                          {r.uploaded ? (
                            <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] text-emerald-800">
                              {r.uploaded} files
                              {r.failed ? ` · ${r.failed} failed` : ""}
                            </span>
                          ) : r.stopped ? (
                            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">
                              on device only
                            </span>
                          ) : (
                            <span className="rounded bg-sky-100 px-1.5 py-0.5 text-[10px] text-sky-800">
                              recording…
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Section>

        {!isReview && (
        <Section className="order-4" title="Gates — what decides a charge">
          <p className="mb-3 text-xs text-slate-500">
            Live values come from the device; a box left blank is not sent. Each
            gate is set from a measurement, and the note under it says which —
            see <code>docs/tasks/12_RECOGNITION_ROADMAP.md</code>.
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {GATE_FIELDS.map((f) => {
              const live = gates ? gates[f.key] : undefined;
              return (
                <label key={f.key} className="block text-xs">
                  <span className="mb-1 flex items-baseline justify-between gap-2">
                    <span className="font-medium text-slate-600">{f.label}</span>
                    {live !== undefined && live !== null && (
                      <span className="font-mono text-[10px] text-slate-400">
                        device: {live}
                      </span>
                    )}
                  </span>
                  <input
                    type="number"
                    step={f.step}
                    min={f.min}
                    max={f.max}
                    placeholder={live !== undefined && live !== null ? String(live) : ""}
                    value={config[f.key] ?? ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      setConfig((c) => {
                        const next = { ...c };
                        if (v === "") delete next[f.key];
                        else next[f.key] = Number(v);
                        return next;
                      });
                    }}
                    className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
                  />
                  <span className="mt-1 block text-[10px] leading-snug text-slate-400">
                    {f.help}
                  </span>
                </label>
              );
            })}
          </div>

          <div className="mt-4 space-y-2 rounded border border-slate-200 bg-slate-50 p-3">
            <label className="flex items-start gap-2 text-xs">
              <input
                type="checkbox"
                checked={config.door_auto_session ?? !!gates?.door_auto_session}
                onChange={(e) => setConfig((c) => ({ ...c, door_auto_session: e.target.checked }))}
                className="mt-0.5"
              />
              <span>
                <strong>Start detection automatically</strong> — open the session
                when the bottom camera stops being dark, close it{" "}
                {gates?.door_close_settle_s ?? 20}s after it goes dark again.
                <span className="block text-[10px] text-slate-500">
                  The signal is light, not a background comparison: a closed
                  cabinet is a dark box, so this needs no reference and a person
                  standing at an open cabinet cannot make it read "closed". Set
                  the two levels above from the live reading in the header
                  before arming this — the shipped defaults are a guess.
                </span>
                <span className="block text-[10px] text-slate-500">
                  Keeps the device awake to watch, which a camera used as a door
                  sensor cannot avoid. A dead camera also reads black and will
                  look like a cabinet nobody opens.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2 text-xs">
              <input
                type="checkbox"
                checked={config.fallback_enabled ?? !!gates?.cloud_fallback_enabled}
                onChange={(e) => setConfig((c) => ({ ...c, fallback_enabled: e.target.checked }))}
                className="mt-0.5"
              />
              <span>
                <strong>Cloud arbiter</strong> — send an abstained episode to the
                cloud for a second opinion.
                <span className="block text-[10px] text-slate-500">
                  The cloud may only confirm what the device already named; a
                  disagreement charges nobody.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2 text-xs">
              <input
                type="checkbox"
                checked={config.projection_enabled ?? !!gates?.projection_enabled}
                onChange={(e) => setConfig((c) => ({ ...c, projection_enabled: e.target.checked }))}
                className="mt-0.5"
              />
              <span>
                <strong>Class-separating projection</strong> — judge identity in
                the projected space.
                <span className="block text-[10px] text-slate-500">
                  Takes effect on the next runtime restart or gallery install,
                  not immediately.
                </span>
              </span>
            </label>
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            <button
              onClick={applyConfig}
              disabled={!deviceId || Object.keys(config).length === 0}
              className="btn-primary text-sm disabled:opacity-50"
            >
              Push {Object.keys(config).length || "no"} change
              {Object.keys(config).length === 1 ? "" : "s"}
            </button>
            <button
              onClick={() => setConfig({})}
              className="rounded border border-slate-300 px-3 py-1 text-sm hover:bg-slate-50"
            >
              Discard edits
            </button>
          </div>
          <p className="mt-2 text-xs text-amber-700">
            Wire-set values are persisted on the cabinet and survive a restart
            and a release (handbook ch. 13); a manual restart of a cabinet
            without the overrides file loses them.
          </p>
        </Section>
        )}
        {isReview && (
          <p className="order-3 text-xs text-slate-500 lg:col-span-2">
            This cabinet runs the opening review: the edge-mode detection controls and gates are hidden, they do nothing here.
            The review's own settings are in the handbook, chapter 17.
          </p>
        )}
      </div>

      <Section title="Pipeline log">
        <div className="min-w-0 max-h-96 overflow-auto rounded border border-slate-200 bg-slate-50 font-mono text-[11px] break-all">
          {pipelineLog.length === 0 ? (
            <div className="p-2 text-slate-500">no entries yet</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {pipelineLog.slice().reverse().map((e, i) => {
                const { ts, stage, event, ...rest } = e;
                const stageName = stage || event || "?";
                const detail = Object.keys(rest).length ? JSON.stringify(rest) : "";
                return (
                  <li key={i} className="px-2 py-1">
                    <span className="text-slate-400">
                      {ts ? new Date(ts * 1000).toLocaleTimeString() : "—"}
                    </span>{" "}
                    <span className="font-medium">{stageName}</span>
                    {detail && (
                      <span className="text-slate-600">{" · "}{detail}</span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </Section>

      <Section title="Recent commands">
        <div className="min-w-0 max-h-80 overflow-auto">
          <table className="w-full text-xs">
            <thead className="text-left text-slate-500">
              <tr>
                <th className="py-1 pr-2">created</th>
                <th className="py-1 pr-2">type</th>
                <th className="py-1 pr-2">status</th>
                <th className="py-1 pr-2">result</th>
              </tr>
            </thead>
            <tbody>
              {recentCommands.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-2 text-slate-500">no commands yet</td>
                </tr>
              )}
              {recentCommands
                .slice()
                .sort((a, b) => (b.created_at || 0) - (a.created_at || 0))
                .map((c) => (
                  <tr key={c.id} className="border-t border-slate-100">
                    <td className="py-1 pr-2 text-slate-500">
                      {c.created_at ? new Date(c.created_at * 1000).toLocaleTimeString() : "—"}
                    </td>
                    <td className="py-1 pr-2 font-mono">{c.command_type}</td>
                    <td className="py-1 pr-2">
                      <span
                        className={`rounded-full px-2 py-0.5 ${
                          c.status === "done"
                            ? "bg-emerald-100 text-emerald-700"
                            : c.status === "error"
                              ? "bg-rose-100 text-rose-700"
                              : "bg-slate-100 text-slate-700"
                        }`}
                      >
                        {c.status}
                      </span>
                    </td>
                    <td className="py-1 pr-2 font-mono">
                      {c.result ? JSON.stringify(c.result).slice(0, 80) : ""}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
}
